import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Menu, X, Globe, Mail, ExternalLink } from 'lucide-react';

const Navigation = () => {
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation(language);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [infoBannerVisible, setInfoBannerVisible] = useState(true);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/windsurfing', label: t('nav.windsurfing') },
    { path: '/kitesurfing', label: t('nav.kitesurfing') },
    { path: '/wingfoiling', label: t('nav.wingfoiling') },
    { path: '/center', label: t('nav.center') },
    { path: '/news', label: t('nav.news') },
    { path: '/live', label: 'Live' },
    { path: '/hotels', label: t('nav.hotels') },
    { path: '/prices', label: t('nav.prices') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const bookingInfo = {
    de: {
      emailText: 'Die Buchung erfolgt direkt über uns per E-Mail:',
      registrationText: 'Bitte zusätzlich hier registrieren:',
      registrationButton: 'Zur Registrierung'
    },
    en: {
      emailText: 'Booking is done directly through us via email:',
      registrationText: 'Please also register here:',
      registrationButton: 'Register Now'
    }
  };

  const info = language === 'de' ? bookingInfo.de : bookingInfo.en;

  return (
    <>
      <nav className="bg-white shadow-md sticky top-0 z-50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src="/logo.png" 
              alt="Surfmotion Soma Bay" 
              className="h-12 w-auto"
              style={{ maxHeight: '48px', width: 'auto' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                className={`px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === item.path
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Language Toggle & Mobile Menu Button */}
          <div className="flex items-center space-x-2 lg:space-x-4">
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center space-x-1 px-2 lg:px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === item.path
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>

    {/* Booking Info Banner */}
    {infoBannerVisible && (
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white py-2 px-4 relative shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-2 md:gap-8 text-sm">
            {/* Email Booking */}
            <div className="flex items-center gap-2">
              <span className="text-base">📧</span>
              <span>{info.emailText}</span>
              <a 
                href="mailto:peter@surfmotion.com" 
                className="font-bold underline hover:text-yellow-200 transition-colors"
              >
                peter@surfmotion.com
              </a>
            </div>
            
            {/* Divider for desktop */}
            <span className="hidden md:block text-white/50">|</span>
            
            {/* Registration Link */}
            <div className="flex items-center gap-2">
              <span className="text-base">📝</span>
              <span>{info.registrationText}</span>
              <a 
                href="https://reservations.surfmotion.com/reservation"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full font-bold text-xs hover:bg-yellow-300 transition-all shadow hover:scale-105"
              >
                {info.registrationButton} →
              </a>
            </div>
          </div>
          
          {/* Close Button */}
          <button 
            onClick={() => setInfoBannerVisible(false)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default Navigation;